import {
  estimateMeterRead,
  submitEstimatedMeterReading,
} from "@/api/backofficeRest/generated/meter-read-estimator/meter-read-estimator"
import {
  EstimateMeterReadRequest,
  EstimateMeterReadResponse,
  EstimateMeterReadRequestFuelType,
  EstimateMeterReadRequestMode,
  EstimatorRegisterInputUnit,
  MeterReading,
} from "@/api/backofficeRest/generated/model"
import { isBackofficeApiError } from "@/api/backofficeRest/backofficeApiError"
import { MeterReadEstimateParser } from "@/models/meterReadEstimate/meterReadEstimate.parser"
import {
  EstimatorFuelType,
  EstimatorMode,
  IEstimateRequest,
  IMeterReadEstimate,
  VolumeUnit,
} from "@/models/meterReadEstimate/meterReadEstimate.types"

/*
  Our enums and the generated ones happen to share their string values, so a
  cast would compile - but it would also keep compiling after codegen dropped or
  renamed a member, which is exactly the divergence worth catching. Record<ours,
  theirs> makes the compiler insist every domain member is mapped, and naming
  each generated member makes a schema change a build failure.
*/
const API_MODE: Record<EstimatorMode, EstimateMeterReadRequestMode> = {
  [EstimatorMode.TwoReads]: EstimateMeterReadRequestMode.TWO_READS,
  [EstimatorMode.KnownReadPlusEacAq]:
    EstimateMeterReadRequestMode.KNOWN_READ_PLUS_EAC_AQ,
}

const API_FUEL_TYPE: Record<
  EstimatorFuelType,
  EstimateMeterReadRequestFuelType
> = {
  [EstimatorFuelType.Electricity]: EstimateMeterReadRequestFuelType.ELECTRICITY,
  [EstimatorFuelType.Gas]: EstimateMeterReadRequestFuelType.GAS,
}

const API_VOLUME_UNIT: Record<VolumeUnit, EstimatorRegisterInputUnit> = {
  [VolumeUnit.CubicMetres]: EstimatorRegisterInputUnit.M3,
  [VolumeUnit.HundredsCubicFeet]: EstimatorRegisterInputUnit.HCF,
  [VolumeUnit.ThousandsCubicFeet]: EstimatorRegisterInputUnit.THCF,
  [VolumeUnit.ThousandsCubicMetres]: EstimatorRegisterInputUnit.TCM3,
}

/**
 * Deliberately a plain promise rather than a vue-query mutation: the estimator
 * recalculates as the agent types, and the form already owns the debounce and
 * the staleness guard.
 */
class MeterReadEstimatorServiceClass {
  async estimate(request: IEstimateRequest): Promise<IMeterReadEstimate> {
    let response: EstimateMeterReadResponse

    try {
      response = await estimateMeterRead(this.toInput(request))
    } catch (error) {
      throw new Error(
        isBackofficeApiError(error)
          ? error.userMessage
          : "The estimator could not be reached",
      )
    }

    if (!response) throw new Error("The estimator returned no response")

    return MeterReadEstimateParser.toDomain(response)
  }

  /**
   * Writes the estimate to Junifer as a System read, quality Estimated
   * (SO-32409). The gateway composes the reading's description from the
   * caller's token and appends `calculationDetail`, so the agent line cannot be
   * spoofed from here. It also refuses a date that already has a read rather
   * than replacing it.
   */
  async submitEstimatedRead(
    billingAccountId: number,
    readings: MeterReading[],
    calculationDetail: string,
  ): Promise<void> {
    try {
      await submitEstimatedMeterReading(billingAccountId, {
        readings,
        calculationDetail,
      })
    } catch (error) {
      throw new Error(
        isBackofficeApiError(error)
          ? error.userMessage
          : "The estimated read could not be submitted",
      )
    }
  }

  private toInput(request: IEstimateRequest): EstimateMeterReadRequest {
    return {
      mode: API_MODE[request.mode],
      fuelType: API_FUEL_TYPE[request.fuelType],
      seasonal: request.seasonal,
      targetDate: request.targetDate,
      calorificValue: request.calorificValue,
      registers: request.registers.map((register) => ({
        registerName: register.registerName,
        read2Value: register.read2Value,
        read2Date: register.read2Date,
        read1Value: register.read1Value,
        read1Date: register.read1Date,
        eacAq: register.eacAq,
        registerDigits: register.registerDigits,
        registerDecimalPlaces: register.registerDecimalPlaces,
        unit: register.unit && API_VOLUME_UNIT[register.unit],
      })),
    }
  }
}

export const MeterReadEstimatorService = new MeterReadEstimatorServiceClass()
